import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAuthStore } from '@/store/authStore'
import { showErrorToast } from '@/utils/error-handler'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useTranslation } from 'react-i18next'

const registerSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  fullName: z.string().optional(),
})

type RegisterFormInput = z.infer<typeof registerSchema>

export default function RegisterPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { register: registerUser, isLoading } = useAuthStore()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormInput>({
    resolver: zodResolver(registerSchema),
  })

  const onSubmit = async (data: RegisterFormInput) => {
    try {
      await registerUser(data.email, data.password, data.fullName)
      // If registration succeeds, user is automatically signed in
      navigate('/scripts', { replace: true })
    } catch (error: any) {
      // Show error message from Supabase or custom error
      const errorMessage = error.message || t('errors.registrationFailed')
      showErrorToast(error, errorMessage)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">{t('auth.createAccount')}</CardTitle>
          <CardDescription className="text-center">
            {t('auth.signUpTo')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="fullName">{t('auth.fullName')} (optional)</Label>
              <Input
                id="fullName"
                type="text"
                {...register('fullName')}
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  {t('common.loading')}
                </>
              ) : (
                t('auth.register')
              )}
            </Button>
            <div className="text-center text-sm">
              <a href="/auth/login" className="text-primary hover:underline">
                {t('auth.login')}
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
